"use client";

import { useState, useRef } from "react";
import { Plus, X, PencilIcon, LucideUser, Eye, EyeOff } from "lucide-react";
import { Button } from "@/templates/Admin/components/Button/Button";
import { Input } from "@/templates/Admin/components/Input/Input"
import { Avatar, AvatarFallback, AvatarImage } from "@/templates/Admin/components/Avatar/Avatar"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/templates/Admin/components/Select/Select";
import { useToast } from "@/templates/Admin/components/Toast/ToastProvider";
import cmcServices from "@/templates/Admin/utils/services/cmcServices";
import { validatePassword, PasswordRequirements, canSubmitForm } from '@/utils/passwordValidation';
import { useIdPath } from "@/contexts/IdPathProvider";

interface AvatarData {
  file: File | null;
  preview: string;
  name: string;
  size: number;
  isUploading: boolean;
  url: string | null;
}

interface CreateUserData {
  firstName: string;
  lastName: string;
  name: string;
  userId: string;
  email: string;
  password: string;
  role: string;
  avatar?: string;
}

interface AddUserFormProps {
  onClose: () => void;
  onAddUser: (user: CreateUserData) => void;
  roleOptions?: { value: string; label: string }[];
  isModal?: boolean;
}

export function AddUserForm({ onClose, onAddUser, roleOptions = [], isModal = false }: AddUserFormProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showSuccess, showError } = useToast();
  const { idPath } = useIdPath();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    userId: "",
    email: "",
    password: "",
    confirmPassword: "",
    role: "public",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [userAvatar, setUserAvatar] = useState<AvatarData | null>(null);

  const handleAvatarUpload = async (file: File) => {
    if (file && file.type.startsWith('image/')) {
      try {
        // Create preview immediately for better UX
        const reader = new FileReader();
        reader.onload = (e) => {
          const avatarData = {
            file: file,
            preview: e.target?.result as string,
            name: file.name,
            size: file.size,
            isUploading: true,
            url: null, // Will be set after S3 upload
          };
          setUserAvatar(avatarData);
        };
        reader.readAsDataURL(file);

        // Upload to S3
        const formData = new FormData();
        formData.append('subfolder', `admin/users/avatars/`);
        formData.append('file', file);

        const uploadResult = await cmcServices.uploadToS3({ body: formData }, idPath);

        // Check if upload failed
        if ('error' in uploadResult) {
          throw new Error(uploadResult.error);
        }

        const avatarUrl = (uploadResult as { data?: { file_url?: string } }).data?.file_url;
        if (!avatarUrl) {
          throw new Error('Không nhận được URL hình ảnh từ S3');
        }

        // Update avatar data with S3 URL
        const finalAvatarData = {
          file: file,
          preview: URL.createObjectURL(file),
          name: file.name,
          size: file.size,
          isUploading: false,
          url: avatarUrl,
        };

        setUserAvatar(finalAvatarData);
        showSuccess('Upload ảnh thành công!', 'Thành công');
      } catch (error: unknown) {
        console.error('Error uploading avatar:', error);
        showError((error as Error).message || 'Có lỗi xảy ra khi upload ảnh', 'Lỗi');

        // Reset uploading state on error
        setUserAvatar((prev: AvatarData | null) => (prev ? { ...prev, isUploading: false } : null));
      }
    }
  };

  const handleRemoveAvatar = () => {
    setUserAvatar(null);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleAvatarUpload(file);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user types
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Required fields
    if (!formData.firstName.trim()) newErrors.firstName = "Họ là bắt buộc";
    if (!formData.lastName.trim()) newErrors.lastName = "Tên là bắt buộc";
    if (!formData.userId.trim()) newErrors.userId = "User ID là bắt buộc";
    if (!formData.email.trim()) newErrors.email = "Email là bắt buộc";
    if (!formData.password) newErrors.password = "Mật khẩu là bắt buộc";
    if (!formData.confirmPassword)
      newErrors.confirmPassword = "Xác nhận mật khẩu là bắt buộc";

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors.email = "Email không hợp lệ";
    }

    // User ID validation (no spaces, special characters)
    const userIdRegex = /^[a-z0-9_.]+$/;
    if (formData.userId && !userIdRegex.test(formData.userId)) {
      newErrors.userId =
        "User ID chỉ được chứa chữ cái thường, số, dấu chấm và gạch dưới";
    }

    // Password validation
    if (formData.password) {
      const passwordValidation = validatePassword(formData.password);
      if (!passwordValidation.isValid) {
        newErrors.password = passwordValidation.errors[0]; // Show first error
      }
    }

    // Confirm password
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Mật khẩu xác nhận không khớp";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      const newUser = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        name: `${formData.firstName} ${formData.lastName}`,
        userId: formData.userId,
        email: formData.email,
        password: formData.password,
        role: formData.role,
        avatar: userAvatar?.url || undefined,
      };

      onAddUser(newUser);
    }
  };

  // Get role label for display
  const getRoleLabel = (roleValue: string) => {
    const role = roleOptions.find(r => r.value === roleValue);
    return role?.label || roleValue;
  };

  return (
    <div className={isModal ? "space-y-6" : "h-full flex flex-col"}>
      {/* Header - only show when not in modal */}
      {!isModal && (
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold">Thêm tài khoản mới</h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-8 w-8"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Avatar */}
      <div className="flex justify-center mb-6">
        <div className="relative">
          <Avatar className="h-24 w-24">
            {userAvatar ? (
              <AvatarImage
                src={userAvatar.preview || userAvatar.url || ""}
                alt=""
                className="object-cover"
              />
            ) : (
              <AvatarFallback className="bg-gray-200">
                <LucideUser className="h-12 w-12 text-gray-400" />
              </AvatarFallback>
            )}
          </Avatar>

          {/* Upload/Edit button */}
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="absolute bottom-0 right-0 h-8 w-8 rounded-full shadow-md"
            onClick={triggerFileInput}
            disabled={userAvatar?.isUploading}
          >
            {userAvatar?.isUploading ? (
              <div className="animate-spin h-4 w-4 border-2 border-gray-300 border-t-gray-600 rounded-full"></div>
            ) : (
              <PencilIcon className="h-4 w-4" />
            )}
          </Button>

          {/* Remove avatar button */}
          {userAvatar && !userAvatar.isUploading && (
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="absolute -top-2 -right-2 h-6 w-6 rounded-full shadow-md bg-red-500 hover:bg-red-600 text-white border-red-500"
              onClick={handleRemoveAvatar}
            >
              <X className="h-3 w-3" />
            </Button>
          )}

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>
      </div>

      {/* Form Fields */}
      <div className={isModal ? "space-y-4" : "space-y-4 flex-1 overflow-y-auto"}>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Họ <span className="text-red-500">*</span>
            </label>
            <Input
              value={formData.firstName}
              onChange={(e) => handleInputChange("firstName", e.target.value)}
              placeholder="Họ của bạn"
              className={errors.firstName ? "border-red-300" : ""}
            />
            {errors.firstName && (
              <p className="text-xs text-red-500 mt-1">{errors.firstName}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tên <span className="text-red-500">*</span>
            </label>
            <Input
              value={formData.lastName}
              onChange={(e) => handleInputChange("lastName", e.target.value)}
              placeholder="Tên của bạn"
              className={errors.lastName ? "border-red-300" : ""}
            />
            {errors.lastName && (
              <p className="text-xs text-red-500 mt-1">{errors.lastName}</p>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tên đăng nhập <span className="text-red-500">*</span>
          </label>
          <Input
            value={formData.userId}
            onChange={(e) => handleInputChange("userId", e.target.value)}
            placeholder="Nhập tên đăng nhập"
            className={errors.userId ? "border-red-300" : ""}
          />
          {errors.userId ? (
            <p className="text-xs text-red-500 mt-1">{errors.userId}</p>
          ) : (
            <p className="text-xs text-gray-500 mt-1">
              Tên đăng nhập không thể thay đổi sau khi tạo
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email <span className="text-red-500">*</span>
          </label>
          <Input
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange("email", e.target.value)}
            placeholder="example@domain.com"
            className={errors.email ? "border-red-300" : ""}
          />
          {errors.email && (
            <p className="text-xs text-red-500 mt-1">{errors.email}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Mật khẩu <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <Input
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={(e) => handleInputChange("password", e.target.value)}
              placeholder="Nhập mật khẩu"
              className={errors.password ? "border-red-300 pr-10" : "pr-10"}
            />
            <button
              type="button"
              className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-gray-50 rounded-r-md"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <PasswordRequirements password={formData.password} />
          {errors.password ? (
            <p className="text-xs text-red-500 mt-1">{errors.password}</p>
          ) : null}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nhập lại mật khẩu <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <Input
              type={showConfirmPassword ? "text" : "password"}
              value={formData.confirmPassword}
              onChange={(e) =>
                handleInputChange("confirmPassword", e.target.value)
              }
              placeholder="Nhập lại mật khẩu"
              className={errors.confirmPassword ? "border-red-300 pr-10" : "pr-10"}
            />
            <button
              type="button"
              className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-gray-50 rounded-r-md"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="text-xs text-red-500 mt-1">
              {errors.confirmPassword}
            </p>
          )}
          {/* Real-time password match validation */}
          {formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword && (
            <p className="text-xs text-red-500 mt-1">Mật khẩu nhập lại chưa chính xác</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Phân quyền <span className="text-red-500">*</span>
          </label>
          <Select
            value={formData.role}
            onValueChange={(value) => handleInputChange("role", value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Chọn quyền">
                {formData.role ? getRoleLabel(formData.role) : "Chọn quyền"}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {roleOptions.map((role) => (
                <SelectItem key={role.value} value={role.value}>
                  {role.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Action Buttons */}
      <div className={isModal ? "flex justify-end gap-3 pt-4 border-t" : "mt-8"}>
        {isModal && (
          <Button
            variant="outline"
            onClick={onClose}
          >
            Hủy
          </Button>
        )}
        <Button
          onClick={handleSubmit}
          disabled={!canSubmitForm(formData.password, formData.confirmPassword) || !formData.firstName || !formData.lastName || !formData.userId || !formData.email}
          className={isModal ? "bg-green-600 hover:bg-green-700" : "w-full bg-green-600 hover:bg-green-700"}
        >
          <Plus className="h-4 w-4 mr-2" />
          Tạo tài khoản mới
        </Button>
      </div>
    </div>
  );
}
